import Api from "../services/AxiosService";
import { unwrap } from "./envelope";

/** One row of the admin all-applications list (svc-06 `ApplicationSummary`). */
export interface ApplicationSummary {
  applicationId: string;
  name: string | null;
  applicationNumber: string | null;
  oem: string | null;
  productionProgram: string | null;
  materialCode: string | null;
  annualVolume: number | null;
  fastenerCount: number;
  createdByEmail: string | null;
  // Owner's company name (joined from the owning user); null when unset.
  ownerCompanyName?: string | null;
  // Creator's email (the staff impersonator on registered Work-For); null for
  // self / unregistered, where the creator is the owner.
  createdByUserEmail?: string | null;
  // Work-For "Other" (unregistered) customer email; the app's owner/customer is
  // shown from this in place of createdByEmail when present.
  workingForEmail?: string | null;
  has3d: boolean;
  /** Current 3D STEP filename (application_files, kind='3d-model'), or null. */
  cad3dFileName: string | null;
  /** 3D analysis status (application_cad3d_analysis.status), e.g. 'ready'/'none'. */
  cad3dStatus: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedApplications {
  items: ApplicationSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface ListApplicationsParams {
  limit?: number;
  offset?: number;
  /** Admin `/all` only — case-insensitive owner email / first / last name match. */
  search?: string;
  /** Admin `/all` only — restrict to applications whose owner is in this region. */
  region?: "EU" | "NA" | "ASIA";
  /** Admin `/all` only — case-insensitive owner-email contains match. */
  userEmail?: string;
}

/**
 * GET /v1/applications/all — admin-only (`canManageUsers`); returns every
 * owner's active applications with the owner email + fastener count.
 */
export async function getAllApplications(
  params: ListApplicationsParams = {},
): Promise<PaginatedApplications> {
  const res = await Api.get("/v1/applications/all", { params });
  return unwrap<PaginatedApplications>(res.data);
}

// ---------------------------------------------------------------------------
// Detail + update (svc-06 `GET /v1/applications/:id`, `PATCH /v1/applications/:id`)
// ---------------------------------------------------------------------------

export type FastenerBrandCode = "PEM" | "SHEREX" | "PROFIL" | "AUTORIV" | "OTHER";

/** A fastener line as returned by the detail endpoint. */
export interface ApplicationFastenerLine {
  fastenerId: string;
  brand: FastenerBrandCode | null;
  type: string | null;
  qtyPerPart: number | null;
  qtyPerPartA: number | null;
  qtyPerPartB: number | null;
}

export interface ApplicationDetail extends ApplicationSummary {
  batchSize: number | null;
  createdBy: string | null;
  fasteners: ApplicationFastenerLine[];
}

/**
 * A fastener line for write. Backend qty XOR (svc-06): each line needs either
 * `qtyPerPart`, OR BOTH `qtyPerPartA` and `qtyPerPartB`; all qty values must be
 * positive; `type` must be a non-empty string or `null`.
 */
export interface ApplicationFastenerLineInput {
  fastenerId?: string;
  brand: FastenerBrandCode;
  type?: string | null;
  qtyPerPart?: number | null;
  qtyPerPartA?: number | null;
  qtyPerPartB?: number | null;
}

/** PATCH body — partial; if `fasteners` is present the whole set is replaced. */
export interface UpdateApplicationBody {
  name?: string;
  applicationNumber?: string | null;
  oem?: string | null;
  productionProgram?: string | null;
  materialCode?: string | null;
  annualVolume?: number | null;
  batchSize?: number | null;
  fasteners?: ApplicationFastenerLineInput[];
}

export async function getApplication(id: string): Promise<ApplicationDetail> {
  const res = await Api.get(`/v1/applications/${id}`);
  return unwrap<ApplicationDetail>(res.data);
}

export async function updateApplication(
  id: string,
  body: UpdateApplicationBody,
): Promise<ApplicationDetail> {
  const res = await Api.patch(`/v1/applications/${id}`, body);
  return unwrap<ApplicationDetail>(res.data);
}

/**
 * DELETE /v1/applications/:id — soft-deletes the application (svc-06; sets
 * `deactivatedAt`/`deactivatedBy`). Admins (canManageUsers) or the owner may
 * delete; the backend returns 204 No Content.
 */
export async function deleteApplication(id: string): Promise<void> {
  await Api.delete(`/v1/applications/${encodeURIComponent(id)}`);
}
